import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useLocation, useNavigate } from 'react-router'

import AuthLayout from '@/components/layout/AuthLayout'
import LoadingScreen from '@/components/feedback/LoadingScreen'
import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { loginSchema, type LoginInput } from '@/schemas/auth'
import type { ApiError } from '@/utils/api'

import './LoginPage.css'

interface LocationState {
  from?: string
}

export default function LoginPage() {
  const { user, login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  if (isLoading) {
    return <LoadingScreen />
  }

  if (user) {
    const from = (location.state as LocationState | null)?.from ?? '/works'
    return <Navigate to={from} replace />
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null)
    try {
      await login(values.email, values.password)
      const from = (location.state as LocationState | null)?.from ?? '/works'
      navigate(from, { replace: true })
    } catch (err) {
      const apiErr = err as ApiError
      setServerError(apiErr?.detail ?? 'Connexion impossible')
    }
  })

  return (
    <AuthLayout eyebrow="Atelier · sgraphic" title="Portail interne">
      <form onSubmit={onSubmit} noValidate className="login-page__form">
        <Field label="Email" required error={errors.email?.message}>
          {(controlProps) => (
            <Input
              type="email"
              autoComplete="email"
              placeholder="atelier@exemple.com"
              {...register('email')}
              {...controlProps}
            />
          )}
        </Field>

        <Field label="Mot de passe" required error={errors.password?.message}>
          {(controlProps) => (
            <Input
              type="password"
              autoComplete="current-password"
              {...register('password')}
              {...controlProps}
            />
          )}
        </Field>

        {serverError && (
          <div className="ui-alert ui-alert--danger" role="alert">
            {serverError}
          </div>
        )}

        <Button type="submit" variant="primary" loading={isSubmitting} block>
          Se connecter
        </Button>
      </form>
    </AuthLayout>
  )
}
